import { useContext, useEffect, useRef, useState } from 'react';
import { NaverLogin, NaverMobileLogin } from '../../assets';
import LoginNaver from 'react-login-by-naver';
import SocialLoginBtn from './SocialLoginBtn';
import { SocialLoginPlatformEnum } from './type';
import { AuthStateContext } from '../../contexts/AuthContext';
import { ModalContext } from '../../contexts/ModalContext';
import { CustomModal } from '../modals/ResponseModal';
import { getAccecssToken, getItem, USERINFO, USERTOKEN } from '../../utils/localStorage';
import { resolvePostAuthRedirect } from '../../utils/postAuthLandingRedirect';
import {
  clearNaverAccessTokenHash,
  completeNaverRedirectLogin,
  getNaverCallbackUrl,
  getNaverClientId,
  hasNaverAccessTokenHash,
  isNaverRedirectDelivered,
  markNaverRedirectDelivered,
  openNaverOAuthTab,
  redirectIfSoundpillLoggedIn,
} from '../../utils/naverCallback';

function NaverWaitingModal({ onConfirm }) {
  return (
    <CustomModal
      title="네이버 로그인"
      info="네이버 로그인을 위해 새 탭을 엽니다."
      content={
        '새 탭에서 가입·로그인을 마친 뒤, 이 창으로 돌아와 확인을 눌러 주세요.\n* 열려 있는 탭에서 로그인을 마쳐주세요.'
      }
      confirmText="확인"
      onClickConfirmFunction={onConfirm}
    />
  );
}

function NaverNotReadyModal({ onConfirm }) {
  return (
    <CustomModal
      title="네이버 로그인"
      info="아직 네이버 로그인이 완료되지 않았습니다."
      content={
        '확인을 누르면 네이버 로그인 탭을 다시 엽니다.\n* 열려 있는 탭에서 로그인을 마쳐주세요.\n새 탭에서 로그인을 마친 뒤, 이 창으로 돌아와 다시 확인해 주세요.'
      }
      confirmText="확인"
      onClickConfirmFunction={onConfirm}
    />
  );
}

export default function NaverLoginBtn({
  afterSocialLoginSuccess,
  dataSocial,
  variant = 'legacy',
  recentLogin,
}) {
  const isRecent = variant === 'draft' && recentLogin === dataSocial;
  const callbackUrl = getNaverCallbackUrl();
  const clientId = getNaverClientId();
  const { handleModal } = useContext(ModalContext);
  const { setLoginSuccess } = useContext(AuthStateContext);
  const onSuccessRef = useRef(afterSocialLoginSuccess);
  onSuccessRef.current = afterSocialLoginSuccess;
  const [isRedirectReturn, setIsRedirectReturn] = useState(() => hasNaverAccessTokenHash());

  /**
   * 모바일은 팝업(새 탭)으로 `/auth#access_token=` 에 도착한다.
   * LoginNaver(isPopup:true)를 다시 init 하면 토큰을 소모하고 로그인이 끝나지 않을 수 있다.
   */
  useEffect(() => {
    if (!isRedirectReturn) return undefined;

    let active = true;
    completeNaverRedirectLogin().then((user) => {
      if (!active) return;

      clearNaverAccessTokenHash();
      setIsRedirectReturn(false);

      if (isNaverRedirectDelivered()) {
        window.dispatchEvent(new Event('naver-redirect-settled'));
        return;
      }

      if (!user?.id) {
        window.dispatchEvent(new Event('naver-redirect-settled'));
        return;
      }

      markNaverRedirectDelivered();
      Promise.resolve(
        onSuccessRef.current({
          type: SocialLoginPlatformEnum.NAVER,
          socialId: user.id,
          email: user.email,
        }),
      )
        .catch((error) => {
          console.error('naver redirect sign-in', error);
        })
        .finally(() => {
          window.dispatchEvent(new Event('naver-redirect-settled'));
        });
    });

    return () => {
      active = false;
    };
  }, [isRedirectReturn]);

  /** 새 탭에서 로그인 후 뒤로가기 등으로 /auth 에 다시 오면 토큰 있으면 메인으로 */
  useEffect(() => {
    if (isRedirectReturn) return undefined;

    const tryRedirect = () => {
      redirectIfSoundpillLoggedIn();
    };

    tryRedirect();
    window.addEventListener('pageshow', tryRedirect);
    window.addEventListener('popstate', tryRedirect);
    return () => {
      window.removeEventListener('pageshow', tryRedirect);
      window.removeEventListener('popstate', tryRedirect);
    };
  }, [isRedirectReturn]);

  const showWaitingModal = () => {
    handleModal(
      <NaverWaitingModal
        onConfirm={() => {
          handleConfirmAfterNaverTab();
        }}
      />,
    );
  };

  const handleConfirmAfterNaverTab = () => {
    const accessToken = getAccecssToken(USERTOKEN);
    const user = getItem(USERINFO);
    // 새 탭이 localStorage 에 심은 세션을 이 탭 AuthContext 에도 반영한 뒤 이동
    if (accessToken && user) {
      setLoginSuccess(user);
      handleModal();
      window.location.assign(resolvePostAuthRedirect());
      return;
    }
    // 미완료 → 확인 시 authorize URL 로 새 탭을 직접 연다 (SDK 히든 클릭은 재오픈이 안 되는 경우가 많음)
    handleModal(
      <NaverNotReadyModal
        onConfirm={() => {
          openNaverOAuthTab();
          showWaitingModal();
        }}
      />,
    );
  };

  const handleNaverClick = (originalOnClick) => () => {
    showWaitingModal();
    // 첫 클릭은 SDK 경로 유지 (기존 플로우), 실패 시 직접 open 도 시도
    originalOnClick?.();
    if (!document.querySelector('#naverIdLogin')?.firstChild) {
      openNaverOAuthTab();
    }
  };

  if (isRedirectReturn) {
    return null;
  }

  return (
    <div
      data-social={dataSocial}
      className={isRecent ? 'login-social-wrap--recent' : undefined}
    >
      <LoginNaver
        clientId={clientId}
        callbackUrl={callbackUrl}
        render={(props) => (
          <>
            <SocialLoginBtn
              onClick={handleNaverClick(props.onClick)}
              backgroundColor="bg-[#03C75A]"
              fontColor="text-white"
              additionalStyle={undefined}
              icon={
                variant === 'draft' ? (
                  <NaverLogin width={24} height={24} />
                ) : (
                  <>
                    <NaverLogin className="hidden md:block" width={24} />
                    <NaverMobileLogin className="block md:hidden" />
                  </>
                )
              }
              text="naver"
              variant={variant}
            />
          </>
        )}
        onSuccess={(result) => {
          afterSocialLoginSuccess({
            type: SocialLoginPlatformEnum.NAVER,
            socialId: result.id,
            email: result.email,
          });
        }}
        onFailure={() => console.error('failed')}
      />
    </div>
  );
}
